import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
} from '@angular/core';
import { BaseComponent } from '@core/base.component';
import { ICON } from '@core/util/icon';
import { BusinessUser } from '@models/businesses';
import { Select } from '@ngxs/store';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { NavigationService } from '@services/nav/nav.service';
import { BusinessUsersDispatch } from '@state/business-users/business-users.dispatch';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { CLOSER_SYNC_DRAWER_VIEW } from '../../closer-sync.component';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { SnackbarService } from '@services/ui/snackbar.service';
import { GlobalFinancialInstitutionsState } from '@state/global-financial-institutions/global-financial-institutions.state';
import { FinancialInstitution } from '@models/lender';

type CurrentStep = {
	stepName: DisconnectFromAssystSteps;
	nextAction?: VoidFunction;
	backAction?: VoidFunction;
	nextActionText?: string;
	backActionText?: string;
};

enum DisconnectFromAssystSteps {
	LSO_FAX_DETECTED = 'lsoFaxDetected',
	LSO_FAX_NOT_DETECTED = 'lsoFaxNotDetected',
	FAX_OR_ASSYST = 'faxOrAssyst',
	DISCONNECT_FROM_ASSYST = 'disconnectFromAssyst',
	ALL_SET_FAX = 'allSetFax',
	ALL_SET_ASSYST = 'allSetAssyst',
}

enum RoutingOptions {
	ASSYST = 'Assyst',
	FAX = 'Fax',
}

type ICurrentStep = Record<DisconnectFromAssystSteps, CurrentStep>;

interface VM {
	instructionRouting: RoutingOptions;
	/**
	 * @description This will always initially render as false, because this is the whole point of the component, to determine wether
	 * or not the user has disconnected from Assyst.
	 * @default false
	 */
	isAssystDisconnected: boolean;
	/**
	 * @description This is used by both the LSO_FAX_DETECTED and LSO_FAX_NOT_DETECTED steps to determine if the user has confirmed the
	 * fax number. In the case of LSO_FAX_DETECTED, this will be set to true if the user confirms the fax number, and in the case of
	 * LSO_FAX_NOT_DETECTED, this will be set to true if the user confirms that the fax number has been updated on LSO.
	 * @default undefined (initially as undefined to prevent the next action from being enabled until the user has made a selection)
	 */
	isFaxNumberConfirmed: boolean;
	hasFaxNumberBeenUpdated: boolean;
	isLoading: boolean;
	loggedInUserId: string;
	lsoFaxNumber?: string;
	stepHistory: CurrentStep[];
}

@Component({
	selector: 'app-disconnect-assyst',
	templateUrl: './disconnect-assyst.component.html',
	styleUrls: ['./disconnect-assyst.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class DisconnectAssystComponent
	extends BaseComponent<VM>
	implements OnInit
{
	@Select(BusinessUsersState.getLoggedInBusinessUser)
	loggedInUser$: Observable<BusinessUser>;
	@SelectSnapshot(GlobalFinancialInstitutionsState.getSyncInstitutions)
	syncInstitutions: FinancialInstitution[];

	@Output() closeDrawer =
		new EventEmitter<CLOSER_SYNC_DRAWER_VIEW.DISCONNECT_ASSYST>();

	@Input() showSyncSuccessState: boolean;

	get SESSION_STORAGE_STEP_HISTORY_KEY() {
		return `assyst-step-history-${this.VM.loggedInUserId}`;
	}

	DisconnectFromAssystSteps = DisconnectFromAssystSteps;
	ALL_STEPS: ICurrentStep = {
		[DisconnectFromAssystSteps.LSO_FAX_DETECTED]: {
			stepName: DisconnectFromAssystSteps.LSO_FAX_DETECTED,
			nextActionText: 'Continue',
			nextAction: () =>
				this.addStepHistory(DisconnectFromAssystSteps.FAX_OR_ASSYST),
		},
		[DisconnectFromAssystSteps.LSO_FAX_NOT_DETECTED]: {
			stepName: DisconnectFromAssystSteps.LSO_FAX_NOT_DETECTED,
			nextActionText: 'Continue',
			nextAction: () =>
				this.addStepHistory(DisconnectFromAssystSteps.FAX_OR_ASSYST),
		},
		[DisconnectFromAssystSteps.FAX_OR_ASSYST]: {
			stepName: DisconnectFromAssystSteps.FAX_OR_ASSYST,
			backActionText: 'Back',
			nextActionText: 'Continue',
			backAction: () => this.removeStepHistory(),
			nextAction: () =>
				this.addStepHistory(
					this.VM.instructionRouting === RoutingOptions.FAX
						? DisconnectFromAssystSteps.ALL_SET_FAX
						: DisconnectFromAssystSteps.DISCONNECT_FROM_ASSYST,
				),
		},
		[DisconnectFromAssystSteps.DISCONNECT_FROM_ASSYST]: {
			stepName: DisconnectFromAssystSteps.DISCONNECT_FROM_ASSYST,
			nextAction: () => {
				this.addStepHistory(DisconnectFromAssystSteps.ALL_SET_ASSYST);
				this.businessUsersDispatch.SetAssystDisconnected(true);
				this.removeSessionStepHistory();
			},
			backAction: () => this.removeStepHistory(),
			nextActionText: 'Connect',
			backActionText: 'Back',
		},
		[DisconnectFromAssystSteps.ALL_SET_ASSYST]: {
			stepName: DisconnectFromAssystSteps.ALL_SET_ASSYST,
			nextAction: () => {
				this.removeSessionStepHistory();
				this.close();
			},
			backAction: () => {},
			nextActionText: 'Done',
			backActionText: '',
		},
		[DisconnectFromAssystSteps.ALL_SET_FAX]: {
			stepName: DisconnectFromAssystSteps.ALL_SET_FAX,
			nextAction: () => {
				this.businessUsersDispatch.SetAssystDisconnected(true);
				this.addStepHistory(DisconnectFromAssystSteps.ALL_SET_ASSYST);
				this.removeSessionStepHistory();
			},
			backAction: () => this.removeStepHistory(),
			nextActionText: 'Connect',
			backActionText: 'Back',
		},
	};

	emailBody = `
	I am emailing to advise that I no longer want to receive mortgage instructions through the Lender Centre portal.
	I am aware of active files that we are completing in the portal but do not want to receive further new instructions.
	Please keep my Lender Centre profile active for the purposes of mortgage discharge requests.
	`;
	private emailSubject = 'Disconnect%20From%20Assyst';
	private emailRecipient = 'UnityLCAdmin@DyeDurham.com';

	mailTo = `mailto:${this.emailRecipient}?subject=${this.emailSubject}&body=${this.emailBody}`;
	closerSyncLenders = [];

	constructor(
		private businessUsersDispatch: BusinessUsersDispatch,
		private navigationService: NavigationService,
		private analytics: AnalyticsService,
		private snackbardService: SnackbarService,
	) {
		super({
			hasFaxNumberBeenUpdated: false,
			instructionRouting: undefined,
			isAssystDisconnected: false,
			isFaxNumberConfirmed: undefined,
			isLoading: false,
			loggedInUserId: null,
			lsoFaxNumber: null,
			stepHistory: [],
		});
	}

	public numberConfirmedRadioOptions = [
		{ label: 'Yes, This Number Is Correct', value: true },
		{ label: 'No, It Needs To Be Updated', value: false },
	];

	public faxNumberUpdatedRadioOptions = [
		{ label: 'Yes, my LSO record needs to be updated', value: true },
		{ label: 'No, I don’t have a fax service ', value: false },
	];

	public instructionRoutingOptions = [
		{ label: 'Lender Centre (Assyst)', value: RoutingOptions.ASSYST },
		{ label: 'Fax', value: RoutingOptions.FAX },
	];

	public ICON = ICON;
	public RoutingOptions = RoutingOptions;
	ngOnInit(): void {
		this.loggedInUser$.pipe(take(1)).subscribe((user) => {
			if (!user?.closerSyncId) {
				this.close();
			}

			this.VM = {
				lsoFaxNumber: user.lsoFaxNumber,
				loggedInUserId: user.id,
			};

			const sessionStorageStepHistory = sessionStorage.getItem(
				this.SESSION_STORAGE_STEP_HISTORY_KEY,
			);
			if (sessionStorageStepHistory) {
				this.VM = {
					stepHistory: sessionStorageStepHistory
						.split(',')
						.map((x) => this.ALL_STEPS[x]),
				};
			} else {
				this.VM = {
					stepHistory: [
						this.ALL_STEPS[
							user.lsoFaxNumber
								? DisconnectFromAssystSteps.LSO_FAX_DETECTED
								: DisconnectFromAssystSteps.LSO_FAX_NOT_DETECTED
						],
					],
				};
			}

			this.closerSyncLenders = this.syncInstitutions
				.filter((inst) => inst.isAssystLender)
				.map((inst) => ({ label: inst.name, imgUrl: inst.logoUrl }));
		});

		if (this.showSyncSuccessState) {
			this.removeSessionStepHistory();
			this.VM = {
				stepHistory: [],
			};
			this.addStepHistory(DisconnectFromAssystSteps.ALL_SET_ASSYST);
		}
	}

	close() {
		this.closeDrawer.emit(CLOSER_SYNC_DRAWER_VIEW.DISCONNECT_ASSYST);
	}

	copyToClipboard() {
		navigator.clipboard.writeText(this.emailBody);
		this.snackbardService.openSnackBar({
			text: 'Copied to clipboard',
			type: 'success',
		});
	}

	get isNextActionDisabled() {
		switch (this.VM.stepHistory.at(-1).stepName) {
			case DisconnectFromAssystSteps.LSO_FAX_DETECTED:
				return (
					this.VM.isFaxNumberConfirmed === undefined ||
					(this.VM.hasFaxNumberBeenUpdated !== true &&
						this.VM.isFaxNumberConfirmed === false)
				);
			case DisconnectFromAssystSteps.LSO_FAX_NOT_DETECTED:
				return (
					!this.VM.hasFaxNumberBeenUpdated || !this.VM.isFaxNumberConfirmed
				);
			case DisconnectFromAssystSteps.FAX_OR_ASSYST:
				return this.VM.instructionRouting === undefined;
			case DisconnectFromAssystSteps.DISCONNECT_FROM_ASSYST:
				return !this.VM.isAssystDisconnected;
			default:
				return false;
		}
	}

	get showFaxNumberConfirmation() {
		return (
			(this.VM.stepHistory.at(-1).stepName ===
				DisconnectFromAssystSteps.LSO_FAX_DETECTED &&
				this.VM.isFaxNumberConfirmed === false) ||
			(this.VM.stepHistory.at(-1).stepName ===
				DisconnectFromAssystSteps.LSO_FAX_NOT_DETECTED &&
				this.VM.isFaxNumberConfirmed)
		);
	}

	private addStepHistory(step: DisconnectFromAssystSteps) {
		if (step === DisconnectFromAssystSteps.ALL_SET_ASSYST) {
			this.analytics.logEvent(AnalyticsEvent.SYNC_INTEGRATIONS_CONNECTED);
		}
		this.VM.stepHistory.push(this.ALL_STEPS[step]);
		this.setSessionStepHistory();
	}

	private removeStepHistory() {
		this.VM.stepHistory.pop();
		this.setSessionStepHistory();
	}

	private setSessionStepHistory() {
		sessionStorage.setItem(
			this.SESSION_STORAGE_STEP_HISTORY_KEY,
			this.VM.stepHistory.map((x) => x.stepName).join(','),
		);
	}

	private removeSessionStepHistory() {
		sessionStorage.removeItem(this.SESSION_STORAGE_STEP_HISTORY_KEY);
	}

	private goToLearnMore() {
		this.removeStepHistory();
		this.addStepHistory(DisconnectFromAssystSteps.DISCONNECT_FROM_ASSYST);
	}

	public updateHasFaxNumberBeenUpdated(event: boolean) {
		this.VM = {
			hasFaxNumberBeenUpdated: event,
		};
	}

	goToTheMortgageInbox() {
		this.navigationService.goToMortgageInbox();
	}
}
